import gql from 'graphql-tag';
import { buildConnectionQuery } from "../../helpers.class";
import { IApiDocumentFilterType } from '../../types/types';

const userFragment = `
  id
  firstName
  lastName
  email
`;

const investigationStaffFragment = `
  InvestigationStaff {
    id
    isPrimary
    isDefault
    clientRequested
    createdAt
    User {
      id
      firstName
      lastName
    }
    Role {
      id
      title
    }
  }
`;

const historyFragment = `
  History {
    id
    comment
    uri
    s3Uri
    status
    createdAt
    ApprovalHistory {
      id
      isApproved
    }
    CreatedBy { ${userFragment} }
  }
`;

const commentsFragment = `
  Comments {
    id
    text
    CreatedBy { ${userFragment} }
    UpdatedBy { ${userFragment} }
    createdAt
    updatedAt
  }
`;

const statusFragment = `
  Status {
    id
    status
    createdAt
  }
`;

const riskTypeFragment = `
  RiskType {
    id
    name
  }
`;

const currentTabIconsFragment = `
  TimeDairyIcon
  DocumentUploaded
  DocumentSent
  EvidenceUploaded
  PhotosUploaded
  PastEvents
  ClientReview
  ReportsWithStatus {
    document {
      title
      status
    }
    latestSnapshot {
      createdAt
    }
    type
  }
`;

const emailsFragment = `
  Emails {
    id
    address
    isPrimary
    Type {
      id
      name
    }
  }
`;

const miscFragment = `
  UpdatedBy { ${userFragment} }
  DocumentCollection { id }
  Shares { id }
`;

const billToFragment = `
    BillTo {
      id
      firstName
      lastName
      middleName
      {{ EMAILS }}
    }
    BillToBranch {
      id
      name
      Company {
        id
        name
      }
    }
`;

const clientFragment = `
    Client {
      id
      firstName
      lastName
      middleName
      {{ EMAILS }}
    }
    ClientBranch {
      id
      name
      Company {
        id
        name
      }
    }
`;

const templateFragment = `
  id
  title
  caption
  uri
  s3Uri
  storedLocation
  Type {
    id
    name
    Category {
      id
      name
    }
  }
  CreatedBy { ${userFragment} }
  Editor { ${userFragment} }
  Investigation {
    {{ INVESTIGATION_STAFF }}
     createdAt
    id
    status
    nefcoNumber
    Priority {
      id
      name
    }
    {{ RISK_TYPE }}
    {{ CLIENT }}
    {{ BILL_TO }}
    LossAddress {
      id
      address1
      address2
      address3
      city
      state
      postal
      country
    }
    Detail {
      claimNumber
      policyNumber
    }
    {{ CURRENT_TAB_ICONS }}
    Insured {
      contactName
      insuredName
    }
  }
  {{ STATUS }}
  {{ HISTORY }}
  {{ COMMENTS }}
  {{ MISC }}
  createdAt
  updatedAt
`;

const investigationStaffRegex = /\{\{\s?INVESTIGATION_STAFF\s?\}\}/g;
const billToRegex = /\{\{\s?BILL_TO\s?\}\}/g;
const clientRegex = /\{\{\s?CLIENT\s?\}\}/g;
const historyRegex = /\{\{\s?HISTORY\s?\}\}/g;
const commentsRegex = /\{\{\s?COMMENTS\s?\}\}/g;
const statusRegex = /\{\{\s?STATUS\s?\}\}/g;
const riskTypeRegex = /\{\{\s?RISK_TYPE\s?\}\}/g;
const emailsRegex = /\{\{\s?EMAILS\s?\}\}/g;
const miscRegex = /\{\{\s?MISC\s?\}\}/g;
const currentTabIconsRegex = /\{\{\s?CURRENT_TAB_ICONS\s?\}\}/g;

const nodeFragment = templateFragment
  .replace(investigationStaffRegex, "")
  .replace(historyRegex, historyFragment)
  .replace(commentsRegex, commentsFragment)
  .replace(statusRegex, statusFragment)
  .replace(riskTypeRegex, riskTypeFragment)
  .replace(clientRegex, clientFragment)
  .replace(billToRegex, billToFragment)
  .replace(emailsRegex, emailsFragment)
  .replace(miscRegex, miscFragment)
  .replace(currentTabIconsRegex, currentTabIconsFragment);

const listReadyForEditorFragment = templateFragment
  .replace(investigationStaffRegex, investigationStaffFragment)
  .replace(historyRegex, "")
  .replace(commentsRegex, "")
  .replace(statusRegex, statusFragment)
  .replace(riskTypeRegex, "")
  .replace(clientRegex, "")
  .replace(billToRegex, billToFragment)
  .replace(emailsRegex, "")
  .replace(miscRegex, "")
  .replace(currentTabIconsRegex, currentTabIconsFragment);

const listTechReviewFragment = templateFragment
  .replace(investigationStaffRegex, investigationStaffFragment)
  .replace(historyRegex, "")
  .replace(commentsRegex, "")
  .replace(statusRegex, statusFragment)
  .replace(riskTypeRegex, "")
  .replace(billToRegex, "")
  .replace(clientRegex, clientFragment)
  .replace(emailsRegex, "")
  .replace(miscRegex, "")
  .replace(currentTabIconsRegex, currentTabIconsFragment);

const listEditorReview = templateFragment
  .replace(investigationStaffRegex, investigationStaffFragment)
  .replace(historyRegex, "")
  .replace(commentsRegex, "")
  .replace(statusRegex, statusFragment)
  .replace(billToRegex, billToFragment)
  .replace(clientRegex, "")
  .replace(riskTypeRegex, "")
  .replace(emailsRegex, "")
  .replace(miscRegex, "")
  .replace(currentTabIconsRegex, currentTabIconsFragment);

export const documentConnectionQuery = (view = null) => {
  if (view && view?.type) {
    switch (view.type) {
      case IApiDocumentFilterType.ViewReadyforeditor:
        return gql`${buildConnectionQuery("Document", "documentConnection", listReadyForEditorFragment)}`;

      case IApiDocumentFilterType.ViewEditorReview:
        return gql`${buildConnectionQuery("Document", "documentConnection", listEditorReview)}`;

      case IApiDocumentFilterType.ViewTechReview:
        return gql`${buildConnectionQuery("Document", "documentConnection", listTechReviewFragment)}`;

      default:
        return gql`${buildConnectionQuery("Document", "documentConnection", nodeFragment)}`;
    }
  } else {
    return gql`${buildConnectionQuery("Document", "documentConnection", nodeFragment)}`;
  }
};

export const addDocumentMutation = gql`mutation AddDocument($input: AddDocumentInput!) {
  addDocument(input: $input) {
    ${nodeFragment}
  }
}`;

export const sentDocumentMutation = gql`mutation AddDocumentSent($input: AddDocumentSentInput!) {
  addDocumentSent(input: $input)
}`;

export const updateDocumentMutation = gql`mutation UpdateDocument($input: UpdateDocumentInput!) {
  updateDocument(input: $input) {
    ${nodeFragment}
  }
}`;

export const removeDocumentMutation = gql`mutation RemoveDocument($id: ID!) {
  removeDocument(id: $id)
}`;

export const generateDocumentShortLinkMutation = gql`mutation GenerateDocumentShortLink($id: ID!) {
  generateDocumentShortLink(id: $id)
}`;

